/**
 * Customer Service Activation Service
 *
 * Handles automatic creation of customer_services records when deals are won
 *
 * Features:
 * - Extract service plans from deal intake
 * - Create customer_services records
 * - Link to customer, deal, and plan
 * - Set up billing cycle and activation dates
 * - Track monthly recurring revenue
 */

import { collection, addDoc, query, where, getDocs, getDoc, updateDoc, doc, QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { db } from '@/firebase';
import type { CustomerService, ServicePlan, Job } from '@/types/schema';
import type { Deal } from '@/types/theo-workflow.types';
import { extractServicePlansFromDeal } from './deal-quote.service';
import { getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';
import { billsRecurringInUtilmate, initialUtilmateHandoff } from './utilmate-handoff.service';
import { isLiveServiceStatus, serviceAddressesAgree } from '@/utils/serviceHealth';

/**
 * Does a deal-less ("orphan") service for the SAME customer refer to the same
 * install as this deal? Duplicate-services bug (Jul 2026): every dedup path
 * queried `where('dealId','==',deal.id)`, so a manually-added service (no
 * dealId, but with an address) was invisible to the deal path — billing then
 * created a second copy. Orphans count as duplicates when:
 *  - they aren't linked to any deal, AND aren't dead (Cancelled/Inactive), AND
 *  - the addresses agree (see serviceAddressesAgree).
 */
export function orphanServiceMatchesDealSite(
  orphan: { dealId?: string | null; address?: string; status?: string },
  dealAddress?: string,
): boolean {
  if (orphan.dealId) return false;
  if (!isLiveServiceStatus(orphan.status)) return false;
  return serviceAddressesAgree(orphan.address, dealAddress);
}

/**
 * Services that would plausibly duplicate one about to be created by hand for
 * this customer: same service type, still live, at a site that agrees.
 *
 * The manual-add path (`AddCustomerServiceModal` → `createCustomerService`) was
 * the ONE creation path with no duplicate check of any kind — every deal-driven
 * path dedups, but a hand-added service checked nothing and linked to no deal.
 * That is how a Starlink kit ended up recorded twice in Aug 2026: auto-created
 * at install completion from the deal, then added again by hand with the
 * retail plan name and price.
 *
 * Unlike the deal-side pool this deliberately DOES match deal-linked services —
 * they are the most likely thing a hand-add collides with.
 */
export async function findPossibleDuplicateServices(
  customerId: string,
  serviceType: string,
  address?: string,
): Promise<Array<CustomerService & { id: string }>> {
  if (!customerId) return [];
  const snap = await getDocs(query(
    collection(db, 'customer_services'),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('customerId', '==', customerId),
  ));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() } as CustomerService & { id: string }))
    .filter((s) =>
      s.serviceType === serviceType &&
      isLiveServiceStatus(s.status) &&
      serviceAddressesAgree(s.address, address));
}

/**
 * Load the dedup pool for a deal's service creation: services already linked
 * to the deal PLUS same-customer orphan services that plausibly refer to the
 * same site (see orphanServiceMatchesDealSite).
 */
async function loadServiceDedupPool(
  dealId: string,
  customerId: string,
  tenantId: string,
  dealAddress?: string,
): Promise<{
  byDealDocs: QueryDocumentSnapshot<DocumentData>[];
  orphanDocs: QueryDocumentSnapshot<DocumentData>[];
}> {
  const [byDealSnap, byCustomerSnap] = await Promise.all([
    getDocs(query(
      collection(db, 'customer_services'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
      where('dealId', '==', dealId),
    )),
    getDocs(query(
      collection(db, 'customer_services'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
      where('customerId', '==', customerId),
    )),
  ]);
  const byDealIds = new Set(byDealSnap.docs.map((d) => d.id));
  const orphanDocs = byCustomerSnap.docs.filter(
    (d) => !byDealIds.has(d.id) && orphanServiceMatchesDealSite(d.data() as CustomerService, dealAddress),
  );
  return { byDealDocs: byDealSnap.docs, orphanDocs };
}

/**
 * Greedy matcher answering "does this incoming service already exist?".
 *
 * Replaces the old `(serviceType, planName)` multiset key (billing audit Jun
 * 2026 HIGH #15, revised Aug 2026). The multiset's COUNTING was right and is
 * kept — as a consumption pool — but keying on planName meant the same
 * physical service recorded under two different plan names never matched. That
 * is how one Starlink kit survived as two records: "Techery Starlink Medium"
 * created from the deal, "STARLINK RESIDENTIAL 200MBPS" added by hand.
 *
 * Identity is `(serviceType, site)` instead — plan name is a label, not an
 * identity. Site agreement goes through `serviceAddressesAgree`, so an
 * address-less auto-created service still matches, while two KNOWN-different
 * addresses never do — which is what keeps multi-property deals creating N
 * records. Matching CONSUMES the doc, so N incoming entries against N existing
 * services pair off 1:1 and an (N+1)th entry is still created.
 */
export function createExistingServiceMatcher<
  T extends { id: string; data(): { serviceType?: string; address?: string } },
>(existingDocs: T[]) {
  const consumed = new Set<string>();
  return {
    /** Consume and return the first unmatched existing service at this site. */
    take(serviceType: string, address?: string): T | undefined {
      const hit = existingDocs.find((d) => {
        if (consumed.has(d.id)) return false;
        const data = d.data();
        return data.serviceType === serviceType
          && serviceAddressesAgree(data.address, address);
      });
      if (hit) consumed.add(hit.id);
      return hit;
    },
  };
}

/**
 * Link an orphan service to the deal it just deduped against, so future runs
 * dedup by dealId directly and the records read as one story.
 */
function adoptOrphanService(orphanId: string, dealId: string): void {
  updateDoc(doc(db, 'customer_services', orphanId), {
    dealId,
    updatedAt: new Date().toISOString(),
  }).catch((err) => console.warn('[Billing Services] Orphan adoption failed:', orphanId, err));
}

/**
 * Activate service plans for a customer when deal is won
 *
 * This function:
 * 1. Extracts service plans from deal intake data
 * 2. Looks up each plan in Firestore by displayName
 * 3. Creates customer_services records for each plan
 * 4. Links to customer, deal, and plan
 * 5. Sets activation date and billing cycle
 *
 * @param deal - The deal that was won
 * @param customerId - Customer ID
 * @param customerName - Customer name for denormalization
 * @param tenantId - Tenant ID for multi-tenancy
 * @returns Created service IDs, plus any plan names that resolved to no price.
 *          An unresolved plan still creates the service — it just has no
 *          monthlyPrice and no planId, which makes it unbillable, invisible in
 *          MRR, and unreachable by the plan-wide price cascade (that query
 *          keys on planId). The caller must surface these; before Aug 2026
 *          this was a console.warn nobody ever read.
 */
export async function activateServicePlansForCustomer(
  deal: Deal,
  customerId: string,
  customerName: string,
  tenantId: string
): Promise<{ createdIds: string[]; unresolvedPlans: string[] }> {
  const extractedPlans = extractServicePlansFromDeal(deal);
  const createdServiceIds: string[] = [];
  const unresolvedPlans: string[] = [];

  // Without a deal id there is nothing to dedup against and nothing to link
  // the service back to — creating one here would be an instant orphan.
  const dealId = deal.id;
  if (!dealId) {
    console.warn('[Customer Activation] Deal has no id — skipping service creation');
    return { createdIds: createdServiceIds, unresolvedPlans };
  }

  if (extractedPlans.length === 0) {
    console.log('[Customer Activation] No service plans found in deal intake');
    return { createdIds: createdServiceIds, unresolvedPlans };
  }

  console.log(`[Customer Activation] Activating ${extractedPlans.length} service plans for customer ${customerName}`);

  // Existing services to dedup against. This used to be a dealId-ONLY query
  // with a per-planId Set and a per-TYPE Set — three problems the billing
  // paths had already fixed and this one hadn't:
  //   - orphans (hand-added, no dealId) were invisible, so a service added
  //     before install completion got a second copy created here;
  //   - the per-TYPE Set dropped the second service of a multi-property deal
  //     (Jun 2026 HIGH #15, fixed for billing only);
  //   - planId identity missed the same service under a different plan.
  // All three paths now share one rule: (serviceType, site), consumed.
  const dealAddress = deal.jobAddress || '';
  const { byDealDocs, orphanDocs } = await loadServiceDedupPool(
    dealId, customerId, tenantId, deal.jobAddress,
  );
  const existingMatcher = createExistingServiceMatcher([...byDealDocs, ...orphanDocs]);
  const orphanIds = new Set(orphanDocs.map((d) => d.id));

  if (byDealDocs.length + orphanDocs.length > 0) {
    console.log(
      `[Customer Activation] ${byDealDocs.length} existing service(s) on deal ${deal.id}` +
      ` + ${orphanDocs.length} unlinked at this site — deduping against both`,
    );
  }

  for (const { planName, serviceType, isInstallationOnly } of extractedPlans) {
    try {
      const mappedType = mapDealCategoryToServiceType(serviceType);
      const isInstallationType = isOneTimeType(mappedType);

      // One identity check for BOTH branches — (type, site), consumed.
      const matched = existingMatcher.take(mappedType, dealAddress);
      if (matched) {
        console.log(
          `[Customer Activation] "${mappedType}" already exists at this site as ` +
          `"${matched.data().planName || 'unnamed plan'}" — skipping`,
        );
        // A hand-added service absorbing this one gets linked to the deal, so
        // the records read as one story and future runs dedup by dealId.
        if (orphanIds.has(matched.id)) adoptOrphanService(matched.id, dealId);
        continue;
      }

      if (isInstallationOnly) {
        // Installation-only service (no plan in Firestore) — WiFi Extension, Security, etc.
        // Extract pricing from deal's approved quote items for this service category
        const installationPrice = getInstallationPriceFromDeal(deal, mappedType);

        const customerService: Omit<CustomerService, 'id'> = {
          customerId,
          customerName,
          serviceType: mappedType,
          tenantId,
          planName,
          dealId: deal.id,
          billingType: 'one-time',
          addressBound: true,
          // ?? not ||: getInstallationPriceFromDeal deliberately preserves a
          // $0 (goodwill / free-of-charge revision). A falsy check turned that
          // into "no price recorded", which reads as an unpriced service
          // rather than a free one.
          installationPrice: installationPrice ?? undefined,
          // Single-phase activation (H7 option a): create directly as Active
          // since this function is only called from effect #10 at install
          // completion. activateServicesOnJobCompletion fills in activation
          // timestamps + Starlink metadata in the same call site.
          status: 'Active',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const cleanService = Object.fromEntries(
          Object.entries(customerService).filter(([, v]) => v !== undefined)
        );

        const docRef = await addDoc(collection(db, 'customer_services'), cleanService);
        createdServiceIds.push(docRef.id);

        console.log(`[Customer Activation] Created installation service ${docRef.id} for "${planName}"`);
        continue;
      }

      // Plan-based service (NBN, Starlink, etc.) — look up the plan in Firestore
      const plan = await findPlanByDisplayName(planName, tenantId);

      if (!plan) {
        console.warn(`[Customer Activation] Plan "${planName}" not found in Firestore — creating service without plan link`);
        // Only recurring services are harmed by this — a one-time install
        // prices off the quote, not the plan.
        if (!isOneTimeType(mappedType)) unresolvedPlans.push(planName);
      }

      // Determine billing type from plan
      const isOneTime = plan?.billingType === 'one-time';

      // Create customer_services record
      const isRecurring = !(isOneTime || isInstallationType);
      const customerService: Omit<CustomerService, 'id'> = {
        customerId,
        customerName,
        serviceType: mappedType,
        tenantId,
        planId: plan?.id,
        planName: plan?.displayName || planName,
        dealId: deal.id,
        billingType: isRecurring ? 'recurring' : 'one-time',
        monthlyPrice: isRecurring ? plan?.sellPrice : undefined,
        billingCycle: isRecurring ? 'monthly' : undefined,
        nextBillingDate: isRecurring ? calculateNextBillingDate() : undefined,
        addressBound: isInstallationType,
        // Single-phase activation (H7 option a): create directly as Active.
        status: 'Active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Recurring services bill in Utilmate — enroll in the hand-off lifecycle
      // exactly like createServicesAtBilling does (closes daily-audit #1204 M1:
      // this path previously skipped enrollment, so services created at install
      // completion never surfaced in the Utilmate drift panel).
      if (isRecurring && billsRecurringInUtilmate(mappedType)) {
        customerService.utilmateHandoff = initialUtilmateHandoff();
      }

      // Strip undefined values before Firestore write
      const cleanService = Object.fromEntries(
        Object.entries(customerService).filter(([, v]) => v !== undefined)
      );

      const docRef = await addDoc(collection(db, 'customer_services'), cleanService);
      createdServiceIds.push(docRef.id);

      console.log(`[Customer Activation] Created customer_service ${docRef.id} for plan "${planName}"`);
    } catch (error) {
      console.error(`[Customer Activation] Failed to create customer_service for plan "${planName}":`, error);
    }
  }

  return { createdIds: createdServiceIds, unresolvedPlans };
}

/** Service types that are one-time installations rather than recurring plans. */
function isOneTimeType(serviceType: string): boolean {
  return ['WiFi Extension', 'Smart Home', 'Security', 'IT'].includes(serviceType);
}

/**
 * Find a service plan by its display name (case-insensitive).
 * Also checks planName and aliases as fallbacks.
 */
async function findPlanByDisplayName(
  displayName: string,
  tenantId: string
): Promise<(ServicePlan & { id: string }) | null> {
  // Fetch all active plans for the tenant and match in-memory (case-insensitive)
  const q = query(
    collection(db, 'service_plans'),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    where('isActive', '==', true)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const plans = snapshot.docs.map(d => ({
    id: d.id,
    ...d.data(),
  })) as (ServicePlan & { id: string })[];

  const lower = displayName.toLowerCase().trim();

  // Priority 1: exact displayName match (case-insensitive)
  let match = plans.find(p => p.displayName?.toLowerCase().trim() === lower);
  if (match) return match;

  // Priority 2: planName match (case-insensitive)
  match = plans.find(p => p.planName?.toLowerCase().trim() === lower);
  if (match) return match;

  // Priority 3: aliases match (case-insensitive)
  match = plans.find(p => p.aliases?.some(a => a.toLowerCase().trim() === lower));
  if (match) return match;

  return null;
}

/**
 * Map deal category to customer service type
 */
function mapDealCategoryToServiceType(dealCategory: string): CustomerService['serviceType'] {
  const mapping: Record<string, CustomerService['serviceType']> = {
    NBN: 'NBN',
    STARLINK: 'Starlink',
    VOIP: 'VoIP',
    IT: 'IT',
    SMART_HOME: 'Smart Home',
    SECURITY: 'Security',
    WIFI_EXTENSION: 'WiFi Extension',
  };

  return mapping[dealCategory] || 'Other';
}

/**
 * Calculate next billing date (30 days from now)
 */
function calculateNextBillingDate(): string {
  const date = new Date();
  date.setDate(date.getDate() + 30);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * Extract total installation price from deal's quote data for a given service type.
 * Checks reviewed items first (post-WON), then falls back to active quote version.
 */
function getInstallationPriceFromDeal(deal: Deal, serviceType: string): number | null {
  // 1. Try reviewed/approved items (most accurate — post-review)
  if (deal.quoteReviewedItems?.length) {
    const approved = deal.quoteReviewedItems.filter(
      (item) => item.approved && item.category?.toLowerCase() === serviceType.toLowerCase()
    );
    if (approved.length > 0) {
      // ?? not || (audit B-M6): an item legitimately revised to $0
      // (free-of-charge goodwill) must not fall back to its original price.
      return approved.reduce(
        (sum: number, item) =>
          sum + (item.revisedTotalPrice ?? item.originalTotalPrice ?? 0),
        0,
      );
    }
  }

  // 2. Try active quote version items.
  // activeQuoteVersion is a 1-BASED VERSION NUMBER (the BP webhook +
  // native acceptance write version: N), not an array index — the old
  // array-index treatment tripped the bounds guard for every
  // webhook-versioned deal and installation services were created
  // without a price (audit B-M6). Look up by version number with the
  // legacy index fallback.
  if (deal.quoteVersions?.length && deal.activeQuoteVersion != null) {
    const activeVersion =
      deal.quoteVersions.find(
        (v) =>
          v.version === deal.activeQuoteVersion ||
          (v as { versionNumber?: number }).versionNumber === deal.activeQuoteVersion,
      ) ?? deal.quoteVersions[deal.activeQuoteVersion];
    if (!activeVersion) {
      console.warn(`[getInstallationPriceFromDeal] activeQuoteVersion ${deal.activeQuoteVersion} matches no version (${deal.quoteVersions.length} versions)`);
      return null;
    }
    if (activeVersion?.items?.length) {
      const items = activeVersion.items.filter(
        (item) => item.category?.toLowerCase() === serviceType.toLowerCase()
      );
      if (items.length > 0) {
        return items.reduce((sum: number, item) => sum + (item.totalPrice || 0), 0);
      }
    }
  }

  // 3. No pricing data found
  return null;
}

/**
 * Activate service plans from confirmed services list (post-service-confirmation).
 * This is the new primary path — called after the office confirms which services
 * are actually being delivered, replacing the old auto-activation at WON.
 */
export async function activateServicePlansFromConfirmedServices(
  deal: Deal,
  customerId: string,
  customerName: string,
  tenantId: string,
  confirmedServices: import('@/types/theo-workflow.types').ConfirmedService[]
): Promise<string[]> {
  const createdServiceIds: string[] = [];

  if (confirmedServices.length === 0) {
    console.log('[Customer Activation] No confirmed services to activate');
    return createdServiceIds;
  }

  console.log(`[Customer Activation] Activating ${confirmedServices.length} confirmed services for customer ${customerName}`);

  // Check for existing services from this deal to prevent duplicates
  const existingQuery = query(
    collection(db, 'customer_services'),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    where('dealId', '==', deal.id)
  );
  const existingSnap = await getDocs(existingQuery);
  const existingServiceTypes = new Set(
    existingSnap.docs.map((d) => d.data().serviceType).filter(Boolean)
  );

  for (const confirmed of confirmedServices) {
    try {
      const mappedType = mapDealCategoryToServiceType(confirmed.serviceType);

      // Skip duplicates
      if (existingServiceTypes.has(mappedType)) {
        console.log(`[Customer Activation] Service type "${mappedType}" already exists for deal — skipping`);
        continue;
      }

      const isInstallationType = isOneTimeType(mappedType);

      if (isInstallationType || confirmed.billingType === 'one-time') {
        // Installation-only service
        // ?? throughout — a confirmed $0 install is "free", not "unpriced".
        const installationPrice =
          confirmed.installationPrice ?? getInstallationPriceFromDeal(deal, mappedType) ?? undefined;

        const customerService: Omit<CustomerService, 'id'> = {
          customerId,
          customerName,
          serviceType: mappedType,
          tenantId,
          planName: confirmed.planName || `${mappedType} Installation`,
          dealId: deal.id,
          billingType: 'one-time',
          addressBound: true,
          installationPrice,
          status: 'Pending Install',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const cleanService = Object.fromEntries(
          Object.entries(customerService).filter(([, v]) => v !== undefined)
        );

        const docRef = await addDoc(collection(db, 'customer_services'), cleanService);
        createdServiceIds.push(docRef.id);
        existingServiceTypes.add(mappedType);
        console.log(`[Customer Activation] Created installation service ${docRef.id} for "${mappedType}"`);
      } else {
        // Plan-based service (NBN, Starlink, VoIP)
        let plan: (ServicePlan & { id: string }) | null = null;

        if (confirmed.planId) {
          // Direct plan lookup by ID
          const planSnap = await getDoc(doc(db, 'service_plans', confirmed.planId));
          if (planSnap.exists()) {
            plan = { id: planSnap.id, ...planSnap.data() } as ServicePlan & { id: string };
          }
        } else if (confirmed.planName) {
          plan = await findPlanByDisplayName(confirmed.planName, tenantId);
        }

        if (!plan) {
          console.warn(`[Customer Activation] Plan "${confirmed.planName}" not found — creating service without plan link`);
        }

        const customerService: Omit<CustomerService, 'id'> = {
          customerId,
          customerName,
          serviceType: mappedType,
          tenantId,
          planId: plan?.id,
          planName: plan?.displayName || confirmed.planName,
          dealId: deal.id,
          billingType: 'recurring',
          // ?? not ||: a confirmed $0/mo (comped, bundled elsewhere) must not
          // silently fall back to the full plan price.
          monthlyPrice: confirmed.monthlyPrice ?? plan?.sellPrice,
          billingCycle: 'monthly',
          nextBillingDate: calculateNextBillingDate(),
          addressBound: false,
          status: 'Pending Install',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const cleanService = Object.fromEntries(
          Object.entries(customerService).filter(([, v]) => v !== undefined)
        );

        const docRef = await addDoc(collection(db, 'customer_services'), cleanService);
        createdServiceIds.push(docRef.id);
        existingServiceTypes.add(mappedType);
        console.log(`[Customer Activation] Created plan service ${docRef.id} for "${confirmed.planName}"`);
      }
    } catch (error) {
      console.error(`[Customer Activation] Failed to create service for "${confirmed.serviceType}":`, error);
    }
  }

  return createdServiceIds;
}

// NOTE: suspendService, cancelService, reactivateService, getActiveServices, calculateMRR
// are all in customer-service.service.ts — the canonical location for service lifecycle operations.
// This file only handles deal→service creation and job→service activation.

/**
 * Run the install-completion side effects on every customer_service for a deal.
 *
 * Called fire-and-forget from effect #10 in job-side-effects.service.ts after
 * an install job reaches COMPLETED. Two responsibilities:
 *   1. Promote any service still at 'Pending Install' (e.g. from the
 *      pre-install service-confirmation flow) to 'Active' and stamp the
 *      activation/installation timestamps.
 *   2. Sync Starlink kit metadata (router/dish/mount serials, kit number,
 *      account email) from provisioning_items onto the customer_service so
 *      the customer record carries the gear actually installed.
 *
 * H7 option (a) — drops the status === 'Pending Install' filter that used to
 * gate this function. With activateServicePlansForCustomer now creating
 * services directly as 'Active' (single-phase), the old filter would have
 * skipped them entirely and the Starlink metadata would never sync. Querying
 * by dealId only finds both newly-Active services AND any legacy Pending
 * Install services that were stuck from a partial-failure two-phase run.
 *
 * Idempotency:
 *   - First-time activation fields (activatedAt, installedAt, installedBy,
 *     jobId) are stamped only when activatedAt is unset, preserving the
 *     historical "first install" timestamp across re-completions of the
 *     same install job (e.g. revert + re-do).
 *   - Starlink metadata is overwritten with the latest provisioning values
 *     each time (deliberate — provisioning is the source of truth).
 */
export async function activateServicesOnJobCompletion(job: Job): Promise<void> {
  // Only install jobs trigger service activation
  if (job.type !== 'Install') return;

  // Need a dealId to find related services
  if (!job.dealId) {
    console.log('[Service Activation] Install job has no dealId — skipping');
    return;
  }

  try {
    // Find all services for this deal (no status filter — see comment above).
    const q = query(
      collection(db, 'customer_services'),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
      where('dealId', '==', job.dealId)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log(`[Service Activation] No services found for deal ${job.dealId}`);
      return;
    }

    const now = new Date().toISOString();
    const techId = job.completedBy || job.techId || '';
    const techName = ''; // Tech name resolved from users collection at display time

    // Fetch provisioning items for this deal to sync Starlink kit data
    const provQuery = query(
      collection(db, 'provisioning_items'),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
      where('dealId', '==', job.dealId)
    );
    const provSnap = await getDocs(provQuery);
    const starlinkKitItem = provSnap.docs
      .map((d) => ({ id: d.id, ...d.data() } as {
        id: string;
        specialWorkflow?: string;
        starlinkEmail?: string;
        starlinkModel?: string;
        starlinkKitNumber?: string;
        starlinkAccountNumber?: string;
        starlinkAddress?: string;
      }))
      .find((item) => item.specialWorkflow === 'kit_order');

    let touched = 0;

    for (const serviceDoc of snapshot.docs) {
      try {
        const serviceData = serviceDoc.data();
        const updates: Record<string, unknown> = {
          updatedAt: now,
        };

        // First-time activation: stamp installation/activation fields only if
        // the service hasn't been activated before. Re-completion via revert
        // path preserves the original activatedAt.
        if (!serviceData.activatedAt) {
          updates.status = 'Active';
          updates.activatedAt = now;
          updates.installedAt = now;
          updates.installedBy = techId;
          updates.installedByName = techName;
          updates.jobId = job.id;
        }

        // Sync Starlink provisioning fields to customer service metadata.
        // Always runs — provisioning is the source of truth and identifiers
        // may have been captured/corrected after initial activation.
        // Router/dish/mount serials were removed (V107.53.x) — we now only
        // track kit number + account number + email + model.
        if (serviceData.serviceType === 'Starlink' && starlinkKitItem) {
          const existingMetadata = serviceData.metadata || {};
          const starlinkMetadata: Record<string, string> = {};
          if (starlinkKitItem.starlinkEmail) starlinkMetadata.starlinkAccountEmail = starlinkKitItem.starlinkEmail;
          if (starlinkKitItem.starlinkModel) starlinkMetadata.starlinkModel = starlinkKitItem.starlinkModel;
          if (starlinkKitItem.starlinkKitNumber) starlinkMetadata.kitNumber = starlinkKitItem.starlinkKitNumber;
          if (starlinkKitItem.starlinkAccountNumber) starlinkMetadata.starlinkAccountNumber = starlinkKitItem.starlinkAccountNumber;
          if (starlinkKitItem.starlinkAddress) starlinkMetadata.serviceAddress = starlinkKitItem.starlinkAddress;

          if (Object.keys(starlinkMetadata).length > 0) {
            updates.metadata = { ...existingMetadata, ...starlinkMetadata };
            console.log(`[Service Activation] Syncing Starlink kit data to service ${serviceDoc.id}:`, starlinkMetadata);
          }
        }

        // Skip the write if nothing changed (only updatedAt would bump).
        const meaningfulKeys = Object.keys(updates).filter((k) => k !== 'updatedAt');
        if (meaningfulKeys.length === 0) continue;

        await updateDoc(doc(db, 'customer_services', serviceDoc.id), updates);
        touched++;
      } catch (err) {
        console.error(`[Service Activation] Failed to update service ${serviceDoc.id}:`, err);
      }
    }

    console.log(`[Service Activation] Updated ${touched}/${snapshot.size} services for deal ${job.dealId} (job ${job.id})`);
  } catch (error) {
    console.error('[Service Activation] Failed to activate services on job completion:', error);
  }
}

// ============================================================================
// Billing-stage service creation
// ============================================================================

export interface BillingServiceResult {
  created: number;
  skipped: number;
  /**
   * Number of service-creation attempts that THREW. Different from
   * `skipped` (which means: existed already / valid no-op). Lets the
   * caller surface a partial-success warning so the office doesn't
   * see a green toast when only half the services were created.
   */
  failed: number;
  /** Per-failure detail for the UI's warning surface. */
  failures: Array<{ planName: string; serviceType: string; message: string }>;
  /**
   * Plan names that resolved to no price — the service was still created, but
   * it is unbillable, counts $0 toward MRR, and (having no planId) can't be
   * fixed by a plan-wide price cascade. Silent before Aug 2026; the office
   * needs to hear about it while the deal is still in front of them.
   */
  unresolvedPlans: string[];
  services: Array<{ id: string; serviceType: string; planName: string; status: string }>;
  /**
   * Set when `createServicesAtBilling` short-circuited because the
   * office previously hit "Skip services" on this deal. The caller
   * uses this to decide whether to show the BillingServicesModal
   * again or move on. `servicesSkippedAtBilling` flag on the deal
   * is the persistent record; this flag exists for the immediate
   * return shape.
   */
  skippedAll?: boolean;
}

/**
 * Create customer services when a deal is marked as billed.
 *
 * At billing stage, the install is complete and we know exactly what was delivered.
 * This creates Active customer_services records for each service in the deal —
 * both recurring (NBN, Starlink, VoIP) and one-time installations (WiFi, Security, etc.).
 *
 * Uses AI to generate a billing note summarising what services were set up.
 *
 * Idempotent — skips services that already exist for this deal.
 */
export async function createServicesAtBilling(
  deal: Deal,
): Promise<BillingServiceResult> {
  const result: BillingServiceResult = {
    created: 0,
    skipped: 0,
    failed: 0,
    failures: [],
    unresolvedPlans: [],
    services: [],
  };

  if (!deal.id || !deal.customerId) {
    console.warn('[Billing Services] Deal missing id or customerId — skipping');
    return result;
  }

  // Honour the explicit "Skip services" choice from the
  // BillingServicesModal. Without this guard, a second Mark-as-Billed
  // press (rebill flow, or user thinks first attempt failed) would
  // re-run service creation behind the office's back. The flag is
  // cleared if the office later opens the modal and creates services
  // manually (handled in createConfirmedServices below).
  if ((deal as Deal & { servicesSkippedAtBilling?: boolean }).servicesSkippedAtBilling === true) {
    console.log(`[Billing Services] Deal ${deal.id} previously marked skip-services — short-circuit`);
    result.skippedAll = true;
    return result;
  }

  const customerName = deal.customerName || deal.contactName || 'Unknown';
  const tenantId = deal.tenantId || getTenantIdSync();
  const now = new Date().toISOString();

  // Check existing services to prevent duplicates — services already on this
  // deal PLUS same-customer orphan services at the same site (duplicate-services
  // fix, Jul 2026: manual adds carry no dealId so the dealId-only query never
  // saw them and billing created a second copy).
  const { byDealDocs, orphanDocs } = await loadServiceDedupPool(
    deal.id, deal.customerId, tenantId, deal.jobAddress,
  );
  const existingDocs = [...byDealDocs, ...orphanDocs];
  const existingMatcher = createExistingServiceMatcher(existingDocs);
  // Orphan ids, so a match that was a deal-less service can be adopted onto
  // this deal (future runs then dedup by dealId directly).
  const orphanIds = new Set(orphanDocs.map((d) => d.id));

  // U6 safety net: legacy data may have services stuck at 'Pending Install'
  // (from before the H7 single-phase activation fix landed, or from any path
  // that bypassed the install-completion side-effect chain). Promote them
  // here so billing isn't gated on the user manually re-running install
  // completion. Uses deal.installCompletedAt for the activation timestamp
  // when available — otherwise falls back to `now` (billing time).
  const installAt = deal.installCompletedAt || now;
  const stuckPending = byDealDocs.filter((d) => d.data().status === 'Pending Install');
  if (stuckPending.length > 0) {
    console.log(`[Billing Services] Promoting ${stuckPending.length} stuck Pending Install service(s) for deal ${deal.id}`);
    for (const stuckDoc of stuckPending) {
      try {
        const stuckData = stuckDoc.data();
        const promoteUpdates: Record<string, unknown> = {
          status: 'Active',
          updatedAt: now,
        };
        if (!stuckData.activatedAt) promoteUpdates.activatedAt = installAt;
        if (!stuckData.installedAt) promoteUpdates.installedAt = installAt;
        await updateDoc(doc(db, 'customer_services', stuckDoc.id), promoteUpdates);
      } catch (err) {
        console.error(`[Billing Services] Failed to promote stuck service ${stuckDoc.id}:`, err);
      }
    }
  }

  // Extract services from the deal
  const extractedPlans = extractServicePlansFromDeal(deal);

  if (extractedPlans.length === 0) {
    console.log('[Billing Services] No services found in deal intake data');
    return result;
  }

  console.log(`[Billing Services] Creating ${extractedPlans.length} services for ${customerName} (deal ${deal.id})`);

  // Get install address from deal
  const address = deal.jobAddress || '';
  const city = deal.jobCity || '';

  for (const { planName, serviceType, isInstallationOnly } of extractedPlans) {
    try {
      const mappedType = mapDealCategoryToServiceType(serviceType);
      const isInstallationType = isOneTimeType(mappedType);

      // Skip duplicates — matched on (type, site) and consumed, so N genuine
      // services still create N records. See createExistingServiceMatcher.
      const matched = existingMatcher.take(mappedType, address);
      if (matched) {
        const matchedData = matched.data();
        console.log(
          `[Billing Services] "${mappedType}" already exists at this site as ` +
          `"${matchedData.planName || 'unnamed plan'}" — skipping`,
        );
        result.skipped++;
        if (orphanIds.has(matched.id)) adoptOrphanService(matched.id, deal.id);
        // Report the doc we actually matched, not the first of its type.
        result.services.push({
          id: matched.id,
          serviceType: mappedType,
          planName: matchedData.planName || planName,
          status: matchedData.status || 'Active',
        });
        continue;
      }

      if (isInstallationOnly || isInstallationType) {
        // One-time installation service (WiFi, Security, Smart Home, IT)
        const installationPrice = getInstallationPriceFromDeal(deal, mappedType);

        const customerService: Record<string, unknown> = {
          customerId: deal.customerId,
          customerName,
          serviceType: mappedType,
          planName,
          dealId: deal.id,
          billingType: 'one-time',
          addressBound: true,
          status: 'Active',
          activatedAt: now,
          address,
          city,
          tenantId,
          createdAt: now,
          updatedAt: now,
        };
        // != null, not truthy — keeps a $0 goodwill install recorded as free.
        if (installationPrice != null) customerService.installationPrice = installationPrice;

        const docRef = await addDoc(collection(db, 'customer_services'), customerService);
        result.created++;
        result.services.push({ id: docRef.id, serviceType: mappedType, planName, status: 'Active' });
        console.log(`[Billing Services] Created installation service ${docRef.id} — ${mappedType}`);
      } else {
        // Recurring plan service (NBN, Starlink, VoIP)
        const plan = await findPlanByDisplayName(planName, tenantId);

        // (Removed an `existingPlanIds.has(plan.id)` short-circuit here: it
        // skipped on plan id alone, with no consumption, so the SECOND
        // property of a multi-property deal on the same plan was dropped —
        // re-introducing the exact bug the multiset was added to fix. The
        // matcher above is the single identity check now.)

        // No plan match → no price. The service is still created, but it is
        // unbillable and (having no planId) unreachable by the plan-wide
        // price cascade. Report it rather than only console.warn-ing.
        if (!plan) result.unresolvedPlans.push(planName);

        const customerService: Record<string, unknown> = {
          customerId: deal.customerId,
          customerName,
          serviceType: mappedType,
          dealId: deal.id,
          billingType: 'recurring',
          addressBound: false,
          status: 'Active',
          activatedAt: now,
          billingCycle: 'monthly',
          nextBillingDate: calculateNextBillingDate(),
          address,
          city,
          tenantId,
          createdAt: now,
          updatedAt: now,
        };
        if (plan) {
          customerService.planId = plan.id;
          customerService.planName = plan.displayName;
          customerService.monthlyPrice = plan.sellPrice;
        } else {
          customerService.planName = planName;
        }

        // Recurring → Utilmate hand-off (UD1). Starlink excluded (UD7).
        if (billsRecurringInUtilmate(mappedType)) {
          customerService.utilmateHandoff = initialUtilmateHandoff();
        }

        const docRef = await addDoc(collection(db, 'customer_services'), customerService);
        result.created++;
        result.services.push({
          id: docRef.id,
          serviceType: mappedType,
          planName: (plan?.displayName || planName),
          status: 'Active',
        });
        console.log(`[Billing Services] Created recurring service ${docRef.id} — ${plan?.displayName || planName}`);
      }
    } catch (error) {
      // Record per-failure detail on the result so the caller can
      // surface a partial-success warning. Previously the error was
      // logged-and-swallowed; the office saw a green toast when only
      // half the services were actually created.
      const message =
        error instanceof Error ? error.message.slice(0, 200) : 'Unknown error';
      result.failed++;
      result.failures.push({
        planName,
        serviceType: mapDealCategoryToServiceType(serviceType),
        message,
      });
      console.error(`[Billing Services] Failed to create service for "${planName}":`, error);
    }
  }

  console.log(
    `[Billing Services] Done: ${result.created} created, ${result.skipped} skipped, ${result.failed} failed` +
      (result.unresolvedPlans.length ? `, ${result.unresolvedPlans.length} unpriced` : ''),
  );
  return result;
}

/**
 * Create customer services from a user-confirmed list.
 *
 * Called after the BillingServicesModal where the office has reviewed,
 * toggled, and adjusted which services to create. Only creates services
 * for the entries passed in (already filtered to enabled ones).
 */
export async function createConfirmedServices(
  deal: Deal,
  confirmedServices: Array<{
    serviceType: string;
    planName: string;
    billingType: 'recurring' | 'one-time';
    planId?: string;
    monthlyPrice?: number;
  }>
): Promise<BillingServiceResult> {
  const result: BillingServiceResult = {
    created: 0,
    skipped: 0,
    failed: 0,
    failures: [],
    unresolvedPlans: [],
    services: [],
  };

  if (!deal.id || !deal.customerId || confirmedServices.length === 0) {
    return result;
  }

  const customerName = deal.customerName || deal.contactName || 'Unknown';
  const tenantId = deal.tenantId || getTenantIdSync();
  const now = new Date().toISOString();
  const address = deal.jobAddress || '';
  const city = deal.jobCity || '';

  // Check existing services to prevent duplicates — deal-linked services PLUS
  // same-customer orphans at the same site (duplicate-services fix, Jul 2026).
  const { orphanDocs: confirmedOrphans, byDealDocs: confirmedByDeal } = await loadServiceDedupPool(
    deal.id, deal.customerId, tenantId, deal.jobAddress,
  );
  const existingConfirmedDocs = [...confirmedByDeal, ...confirmedOrphans];
  // (type, site) consuming matcher — billing audit Jun 2026 HIGH #15, revised
  // Aug 2026. The office explicitly confirmed this list in
  // BillingServicesModal, so N entries must still create N records; the
  // matcher consumes to preserve that. Keying on planName (the old shape)
  // meant one service under two names never matched and duplicated instead.
  const confirmedMatcher = createExistingServiceMatcher(existingConfirmedDocs);
  const confirmedOrphanIds = new Set(confirmedOrphans.map((d) => d.id));

  for (const entry of confirmedServices) {
    const serviceType = entry.serviceType;

    const matched = confirmedMatcher.take(serviceType, address);
    if (matched) {
      const matchedData = matched.data();
      result.skipped++;
      if (confirmedOrphanIds.has(matched.id)) adoptOrphanService(matched.id, deal.id);
      // Report the doc we actually matched, not the first of its type.
      result.services.push({
        id: matched.id,
        serviceType,
        planName: matchedData.planName || entry.planName,
        status: matchedData.status || 'Active',
      });
      continue;
    }

    try {
      if (entry.billingType === 'one-time') {
        const installationPrice = getInstallationPriceFromDeal(deal, serviceType);
        const customerService: Record<string, unknown> = {
          customerId: deal.customerId,
          customerName,
          serviceType,
          planName: entry.planName,
          dealId: deal.id,
          billingType: 'one-time',
          addressBound: true,
          status: 'Active',
          activatedAt: now,
          address,
          city,
          tenantId,
          createdAt: now,
          updatedAt: now,
        };
        // != null, not truthy — keeps a $0 goodwill install recorded as free.
        if (installationPrice != null) customerService.installationPrice = installationPrice;

        const docRef = await addDoc(collection(db, 'customer_services'), customerService);
        result.created++;
        result.services.push({ id: docRef.id, serviceType, planName: entry.planName, status: 'Active' });
      } else {
        // Recurring service — use planId if provided, otherwise look up by name
        let planId = entry.planId;
        let planDisplayName = entry.planName;
        let planPrice = entry.monthlyPrice;

        if (planId) {
          // Direct lookup by ID — most reliable
          try {
            const planSnap = await getDoc(doc(db, 'service_plans', planId));
            if (planSnap.exists()) {
              const planData = planSnap.data() as ServicePlan;
              planDisplayName = planData.displayName || planDisplayName;
              planPrice = planPrice ?? planData.sellPrice;
            }
          } catch (err) {
            console.warn(`[Billing Services] Failed to fetch plan ${planId}:`, err);
          }
        } else {
          // Fallback: find by name (case-insensitive)
          const plan = await findPlanByDisplayName(entry.planName, tenantId);
          if (plan) {
            planId = plan.id;
            planDisplayName = plan.displayName;
            // ?? — mirror the planId branch above. An explicit price on the
            // entry is the office's decision and must survive a name match.
            planPrice = planPrice ?? plan.sellPrice;
          }
        }
        // A recurring service with no resolvable price is unbillable and
        // invisible in MRR — and with no planId it can't even be repaired by
        // a plan-wide price cascade (that query keys on planId). Report it.
        if (planPrice == null) result.unresolvedPlans.push(entry.planName);

        const customerService: Record<string, unknown> = {
          customerId: deal.customerId,
          customerName,
          serviceType,
          dealId: deal.id,
          billingType: 'recurring',
          addressBound: false,
          status: 'Active',
          activatedAt: now,
          billingCycle: 'monthly',
          nextBillingDate: calculateNextBillingDate(),
          address,
          city,
          tenantId,
          createdAt: now,
          updatedAt: now,
        };
        if (planId) customerService.planId = planId;
        customerService.planName = planDisplayName;
        if (planPrice != null) customerService.monthlyPrice = planPrice;

        // Recurring services that bill in Utilmate (UD1) get a hand-off
        // lifecycle stamped on creation — the office sets them up in
        // Utilmate manually, then confirms via the "Set up in Utilmate"
        // action. Starlink is excluded (its own feeder, UD7).
        if (billsRecurringInUtilmate(serviceType)) {
          customerService.utilmateHandoff = initialUtilmateHandoff();
        }

        const docRef = await addDoc(collection(db, 'customer_services'), customerService);
        result.created++;
        result.services.push({
          id: docRef.id,
          serviceType,
          planName: planDisplayName,
          status: 'Active',
        });
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message.slice(0, 200) : 'Unknown error';
      result.failed++;
      result.failures.push({
        planName: entry.planName,
        serviceType,
        message,
      });
      console.error(`[Billing Services] Failed to create confirmed service "${entry.planName}":`, error);
    }
  }

  // Clear the skip-services flag if any services were actually created.
  // If the office previously hit "Skip" but later re-opened the modal
  // and created services, the audit-trail flag should reflect that
  // services DO exist for this deal now. Best-effort: a failure here
  // shouldn't block the otherwise-successful creation.
  if (result.created > 0) {
    try {
      const dealAny = deal as Deal & { servicesSkippedAtBilling?: boolean };
      if (dealAny.servicesSkippedAtBilling === true && deal.id) {
        await updateDoc(doc(db, 'deals', deal.id), {
          servicesSkippedAtBilling: false,
          updatedAt: now,
        });
      }
    } catch (err) {
      console.warn('[Billing Services] Failed to clear skip flag:', err);
    }
  }

  console.log(
    `[Billing Confirmed] ${result.created} created, ${result.skipped} skipped, ${result.failed} failed`,
  );
  return result;
}
